import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, Routes, Route } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, Calendar, Newspaper, Users, Mail, Heart, BadgeDollarSign, Trophy, Building2, Image as ImageIcon, BarChart3, Plus, Trash2, Loader2, ShoppingBag, Package, Truck } from "lucide-react";
import api from "@/lib/api";

export function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bcol-navy)] flex items-center justify-center px-5" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <Link to="/" className="block text-center mb-8" data-testid="admin-login-logo">
          <img src="https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/l7wwvehd_ChatGPT%20Image%20May%2028%2C%202026%2C%2002_42_51%20PM.png"
            alt="BCOL" className="h-24 w-24 mx-auto bg-white rounded-full p-2" />
        </Link>
        <form onSubmit={submit} className="bg-white rounded-2xl p-8 shadow-2xl space-y-5">
          <h1 className="font-display text-3xl text-[var(--bcol-navy)]">Admin Login</h1>
          <p className="text-sm text-[var(--bcol-muted)]">Sign in to manage events, posts, donations and more.</p>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--bcol-muted)] mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[var(--bcol-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--bcol-navy)]"
              data-testid="admin-login-email" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[var(--bcol-muted)] mb-2">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border border-[var(--bcol-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--bcol-navy)]"
              data-testid="admin-login-password" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <button disabled={loading} className="btn-primary w-full justify-center" data-testid="admin-login-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-sm">
      <Icon className="w-4 h-4" /> {label}
    </Link>
  );
}

function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex bg-[var(--bcol-bg)]" data-testid="admin-shell">
      <aside className="w-64 bg-[var(--bcol-navy)] text-white p-5 hidden md:flex flex-col">
        <Link to="/admin" className="flex items-center gap-3 mb-8">
          <img src="https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/l7wwvehd_ChatGPT%20Image%20May%2028%2C%202026%2C%2002_42_51%20PM.png"
            alt="BCOL" className="h-10 w-10 bg-white rounded-full p-1 object-contain" />
          <span className="font-display text-xl text-[var(--bcol-gold)]">BCOL Admin</span>
        </Link>
        <nav className="space-y-1 flex-1">
          <NavItem to="/admin" icon={LayoutDashboard} label="Overview" testid="admin-nav-overview" />
          <NavItem to="/admin/events" icon={Calendar} label="Events" testid="admin-nav-events" />
          <NavItem to="/admin/blog" icon={Newspaper} label="News & Blog" testid="admin-nav-blog" />
          <NavItem to="/admin/gallery" icon={ImageIcon} label="Gallery" testid="admin-nav-gallery" />
          <NavItem to="/admin/partners" icon={Building2} label="Partners / Sponsors" testid="admin-nav-partners" />
          <NavItem to="/admin/products" icon={Package} label="Products" testid="admin-nav-products" />
          <NavItem to="/admin/orders" icon={Truck} label="Orders" testid="admin-nav-orders" />
          <NavItem to="/admin/donations" icon={Heart} label="Donations" testid="admin-nav-donations" />
          <NavItem to="/admin/volunteers" icon={Users} label="Volunteers" testid="admin-nav-volunteers" />
          <NavItem to="/admin/memberships" icon={BadgeDollarSign} label="Memberships" testid="admin-nav-memberships" />
          <NavItem to="/admin/nominations" icon={Trophy} label="Nominations" testid="admin-nav-nominations" />
          <NavItem to="/admin/sponsors" icon={Building2} label="Sponsors" testid="admin-nav-sponsors" />
          <NavItem to="/admin/contacts" icon={Mail} label="Contact Msgs" testid="admin-nav-contacts" />
          <NavItem to="/admin/newsletter" icon={Mail} label="Newsletter" testid="admin-nav-newsletter" />
        </nav>
        <div className="mt-auto border-t border-white/10 pt-4">
          <div className="text-xs text-white/60 mb-3">{user.email}</div>
          <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center justify-center gap-2 text-sm bg-white/10 hover:bg-white/20 rounded-lg py-2" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">{children}</main>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  const items = [
    { l: "Donations (paid)", v: stats ? `$${stats.donations_total.toLocaleString()}` : "—", k: "donations_total" },
    { l: "Successful gifts", v: stats?.donations_count ?? "—", k: "donations_count" },
    { l: "Shop revenue", v: stats ? `$${stats.shop_revenue.toLocaleString()}` : "—", k: "shop_revenue" },
    { l: "Shop orders", v: stats?.shop_orders ?? "—", k: "shop_orders" },
    { l: "Products", v: stats?.products ?? "—", k: "products" },
    { l: "Volunteers", v: stats?.volunteers ?? "—", k: "volunteers" },
    { l: "Memberships", v: stats?.memberships ?? "—", k: "memberships" },
    { l: "Newsletter subs", v: stats?.newsletter ?? "—", k: "newsletter" },
    { l: "Events", v: stats?.events ?? "—", k: "events" },
    { l: "Blog posts", v: stats?.blog ?? "—", k: "blog" },
    { l: "Sponsors interest", v: stats?.sponsors ?? "—", k: "sponsors" },
    { l: "Nominations", v: stats?.nominations ?? "—", k: "nominations" },
    { l: "Contact msgs", v: stats?.contacts ?? "—", k: "contacts" },
  ];
  return (
    <div data-testid="admin-overview">
      <h1 className="font-display text-4xl text-[var(--bcol-navy)] mb-2">Overview</h1>
      <p className="text-[var(--bcol-muted)] mb-8">A snapshot of what's happening at BCOL.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {items.map((it) => (
          <div key={it.k} className="card-soft p-5" data-testid={`stat-${it.k}`}>
            <div className="text-xs uppercase tracking-widest text-[var(--bcol-muted)]">{it.l}</div>
            <div className="font-display text-3xl text-[var(--bcol-navy)] mt-2">{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimpleListPage({ title, endpoint, columns, testid }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get(endpoint).then(r => setRows(r.data || [])).finally(() => setLoading(false)); }, [endpoint]);
  return (
    <div data-testid={testid}>
      <h1 className="font-display text-4xl text-[var(--bcol-navy)] mb-6">{title}</h1>
      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bcol-bg)] text-[var(--bcol-muted)] text-xs uppercase tracking-widest">
              <tr>{columns.map((c) => <th key={c.k} className="text-left px-5 py-3">{c.l}</th>)}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length} className="p-6 text-center text-[var(--bcol-muted)]">Loading...</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={columns.length} className="p-6 text-center text-[var(--bcol-muted)]">No records yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id || r.session_id} className="border-t border-[var(--bcol-border)]">
                  {columns.map((c) => <td key={c.k} className="px-5 py-3 text-[var(--bcol-text)]">{c.r ? c.r(r) : String(r[c.k] ?? "")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ManagedListPage({ title, endpoint, fields, displayCols, testid }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => api.get(endpoint).then(r => setRows(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoint, form);
      toast.success("Created");
      setForm({}); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`${endpoint}/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid={testid}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-display text-4xl text-[var(--bcol-navy)]">{title}</h1>
        <button onClick={() => setOpen(!open)} className="btn-primary" data-testid={`${testid}-new`}><Plus className="w-4 h-4" /> New</button>
      </div>
      {open && (
        <form onSubmit={create} className="card-soft p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid={`${testid}-form`}>
          {fields.map((f) => (
            <label key={f.k} className={`block ${f.full ? "sm:col-span-2" : ""}`}>
              <div className="text-xs uppercase tracking-widest text-[var(--bcol-muted)] mb-2">{f.label}</div>
              {f.type === "textarea" ? (
                <textarea required={f.required} rows={4} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full border border-[var(--bcol-border)] rounded-xl px-3 py-2 focus:outline-none focus:border-[var(--bcol-navy)]" />
              ) : (
                <input type={f.type || "text"} required={f.required} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full border border-[var(--bcol-border)] rounded-xl px-3 py-2 focus:outline-none focus:border-[var(--bcol-navy)]" />
              )}
            </label>
          ))}
          <div className="sm:col-span-2 flex gap-3 justify-end">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline">Cancel</button>
            <button disabled={loading} className="btn-primary" data-testid={`${testid}-save`}>{loading ? "Saving..." : "Save"}</button>
          </div>
        </form>
      )}
      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bcol-bg)] text-[var(--bcol-muted)] text-xs uppercase tracking-widest">
              <tr>{displayCols.map(c => <th key={c.k} className="text-left px-5 py-3">{c.l}</th>)}<th></th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={displayCols.length + 1} className="p-6 text-center text-[var(--bcol-muted)]">No items yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[var(--bcol-border)]">
                  {displayCols.map(c => <td key={c.k} className="px-5 py-3">{c.r ? c.r(r) : String(r[c.k] ?? "")}</td>)}
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => del(r.id)} className="text-red-600 hover:text-red-800" data-testid={`${testid}-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get("/orders").then(r => setOrders(r.data || []));
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const updateStatus = async (orderId, fulfillment_status, tracking_number) => {
    try {
      await api.patch(`/orders/${orderId}/fulfill`, { fulfillment_status, tracking_number: tracking_number || null });
      toast.success("Order updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-4xl text-[var(--bcol-navy)] mb-6">Orders</h1>
      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bcol-bg)] text-[var(--bcol-muted)] text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Order #</th>
                <th className="text-left px-5 py-3">Customer</th>
                <th className="text-left px-5 py-3">Total</th>
                <th className="text-left px-5 py-3">Payment</th>
                <th className="text-left px-5 py-3">Fulfillment</th>
                <th className="text-left px-5 py-3">Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-6 text-center text-[var(--bcol-muted)]">Loading…</td></tr>}
              {!loading && orders.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[var(--bcol-muted)]">No orders yet.</td></tr>}
              {orders.map((o) => (
                <React.Fragment key={o.id}>
                  <tr className="border-t border-[var(--bcol-border)]">
                    <td className="px-5 py-3 font-mono text-xs">#{o.id.slice(0,8).toUpperCase()}</td>
                    <td className="px-5 py-3">
                      <div className="text-[var(--bcol-navy)]">{o.customer_name}</div>
                      <div className="text-xs text-[var(--bcol-muted)]">{o.customer_email}</div>
                    </td>
                    <td className="px-5 py-3 font-semibold">${(o.total ?? 0).toFixed(2)}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{o.payment_status}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${o.fulfillment_status === "shipped" ? "bg-blue-100 text-blue-700" : o.fulfillment_status === "delivered" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>{o.fulfillment_status}</span>
                    </td>
                    <td className="px-5 py-3 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="text-[var(--bcol-navy)] hover:underline text-xs" data-testid={`order-toggle-${o.id}`}>
                        {expanded === o.id ? "Hide" : "Details"}
                      </button>
                    </td>
                  </tr>
                  {expanded === o.id && (
                    <tr className="bg-[var(--bcol-bg)]">
                      <td colSpan={7} className="px-5 py-5">
                        <div className="grid lg:grid-cols-3 gap-6">
                          <div>
                            <div className="text-xs uppercase tracking-widest text-[var(--bcol-muted)] mb-2">Items</div>
                            <ul className="space-y-1 text-sm">
                              {(o.items || []).map((it, i) => (
                                <li key={i}>{it.quantity}× {it.name} — ${it.line_total.toFixed(2)}</li>
                              ))}
                            </ul>
                            <div className="mt-2 text-xs text-[var(--bcol-muted)]">Subtotal ${o.subtotal?.toFixed(2)} · Shipping {o.shipping_fee === 0 ? "Free" : `$${o.shipping_fee?.toFixed(2)}`}</div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-widest text-[var(--bcol-muted)] mb-2">Shipping address</div>
                            <div className="text-sm">
                              <div>{o.customer_name}</div>
                              <div>{o.shipping_address}</div>
                              <div>{o.shipping_city}, {o.shipping_state} {o.shipping_zip}</div>
                              <div>{o.shipping_country}</div>
                              {o.phone && <div className="text-xs text-[var(--bcol-muted)] mt-1">{o.phone}</div>}
                            </div>
                            {o.notes && <div className="mt-3 text-xs text-[var(--bcol-muted)] italic">Note: {o.notes}</div>}
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-widest text-[var(--bcol-muted)] mb-2">Update fulfillment</div>
                            <FulfillmentControls order={o} onUpdate={updateStatus} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FulfillmentControls({ order, onUpdate }) {
  const [status, setStatus] = useState(order.fulfillment_status || "pending");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  return (
    <div className="space-y-2">
      <select value={status} onChange={(e) => setStatus(e.target.value)}
        className="w-full border border-[var(--bcol-border)] rounded-xl px-3 py-2 text-sm" data-testid={`fulfill-status-${order.id}`}>
        <option value="pending">Pending</option>
        <option value="shipped">Shipped</option>
        <option value="delivered">Delivered</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number (optional)"
        className="w-full border border-[var(--bcol-border)] rounded-xl px-3 py-2 text-sm" data-testid={`fulfill-tracking-${order.id}`} />
      <button onClick={() => onUpdate(order.id, status, tracking)} className="btn-primary w-full justify-center !py-2 text-xs" data-testid={`fulfill-save-${order.id}`}>Save</button>
    </div>
  );
}

export default function AdminApp() {
  return (
    <AdminShell>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="products" element={
          <ManagedListPage title="Shop Products" endpoint="/products" testid="admin-products"
            fields={[
              { k: "name", label: "Name", required: true },
              { k: "category", label: "Category (Apparel/Tickets/Cultural/Other)", required: true },
              { k: "price", label: "Price (USD)", required: true, type: "number" },
              { k: "stock", label: "Stock (optional)", type: "number" },
              { k: "image_url", label: "Image URL", required: true, full: true },
              { k: "description", label: "Description", type: "textarea", required: true, full: true },
            ]}
            displayCols={[
              { k: "image_url", l: "Image", r: (r) => <img src={r.image_url} className="w-12 h-12 rounded object-cover" alt="" /> },
              { k: "name", l: "Name" },
              { k: "category", l: "Category" },
              { k: "price", l: "Price", r: (r) => `$${Number(r.price).toFixed(2)}` },
              { k: "stock", l: "Stock", r: (r) => r.stock ?? "—" },
            ]} />
        } />
        <Route path="events" element={
          <ManagedListPage title="Events" endpoint="/events" testid="admin-events"
            fields={[
              { k: "title", label: "Title", required: true },
              { k: "date", label: "Date (ISO)", required: true, type: "datetime-local" },
              { k: "location", label: "Location", required: true },
              { k: "category", label: "Category" },
              { k: "image_url", label: "Image URL", full: true },
              { k: "description", label: "Description", type: "textarea", required: true, full: true },
            ]}
            displayCols={[
              { k: "title", l: "Title" }, { k: "category", l: "Category" }, { k: "location", l: "Location" },
              { k: "date", l: "Date", r: (r) => new Date(r.date).toLocaleDateString() },
            ]} />
        } />
        <Route path="blog" element={
          <ManagedListPage title="News & Blog" endpoint="/blog" testid="admin-blog"
            fields={[
              { k: "title", label: "Title", required: true, full: true },
              { k: "author", label: "Author", required: true },
              { k: "tag", label: "Tag" },
              { k: "image_url", label: "Image URL", full: true },
              { k: "excerpt", label: "Excerpt", type: "textarea", required: true, full: true },
              { k: "content", label: "Content", type: "textarea", required: true, full: true },
            ]}
            displayCols={[
              { k: "title", l: "Title" }, { k: "author", l: "Author" }, { k: "tag", l: "Tag" },
              { k: "created_at", l: "Created", r: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />
        } />
        <Route path="gallery" element={
          <ManagedListPage title="Photo Gallery" endpoint="/gallery" testid="admin-gallery"
            fields={[
              { k: "title", label: "Title", required: true },
              { k: "caption", label: "Caption" },
              { k: "image_url", label: "Image URL", required: true, full: true },
            ]}
            displayCols={[
              { k: "title", l: "Title" },
              { k: "image_url", l: "Image", r: (r) => <img src={r.image_url} className="w-12 h-12 rounded object-cover" alt="" /> },
              { k: "caption", l: "Caption" },
            ]} />
        } />
        <Route path="partners" element={
          <ManagedListPage title="Partners & Sponsors (logos)" endpoint="/partners" testid="admin-partners"
            fields={[
              { k: "name", label: "Partner name", required: true },
              { k: "logo_url", label: "Logo URL", required: true, full: true },
              { k: "website_url", label: "Website URL (optional)", full: true },
              { k: "tier", label: "Tier (Bronze/Silver/Gold/Platinum/Partner — optional)" },
            ]}
            displayCols={[
              { k: "logo_url", l: "Logo", r: (r) => <img src={r.logo_url} className="h-10 max-w-[100px] object-contain" alt="" /> },
              { k: "name", l: "Name" },
              { k: "tier", l: "Tier", r: (r) => r.tier || "—" },
              { k: "website_url", l: "Website", r: (r) => r.website_url ? <a href={r.website_url} target="_blank" rel="noopener noreferrer" className="text-[var(--bcol-navy)] underline text-xs">Visit</a> : "—" },
            ]} />
        } />
        <Route path="orders" element={<OrdersPage />} />

        <Route path="donations" element={
          <SimpleListPage title="Donations" endpoint="/donations" testid="admin-donations"
            columns={[
              { k: "amount", l: "Amount", r: (r) => `$${(r.amount ?? 0).toFixed(2)}` },
              { k: "payment_status", l: "Status" },
              { k: "donor", l: "Donor", r: (r) => r.metadata?.donor_name || "Anonymous" },
              { k: "email", l: "Email", r: (r) => r.metadata?.donor_email || "—" },
              { k: "created_at", l: "Date", r: (r) => new Date(r.created_at).toLocaleString() },
            ]} />
        } />
        <Route path="volunteers" element={
          <SimpleListPage title="Volunteers" endpoint="/volunteers" testid="admin-volunteers"
            columns={[
              { k: "full_name", l: "Name" }, { k: "email", l: "Email" }, { k: "phone", l: "Phone" },
              { k: "interests", l: "Interests" },
              { k: "created_at", l: "Submitted", r: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />
        } />
        <Route path="memberships" element={
          <SimpleListPage title="Memberships" endpoint="/memberships" testid="admin-memberships"
            columns={[
              { k: "full_name", l: "Name" }, { k: "email", l: "Email" }, { k: "membership_type", l: "Type" },
              { k: "created_at", l: "Date", r: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />
        } />
        <Route path="nominations" element={
          <SimpleListPage title="Graduate Nominations" endpoint="/nominations" testid="admin-nominations"
            columns={[
              { k: "nominee_name", l: "Nominee" }, { k: "school", l: "School" }, { k: "graduation_year", l: "Year" },
              { k: "nominator_name", l: "By" },
              { k: "created_at", l: "Date", r: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />
        } />
        <Route path="sponsors" element={
          <SimpleListPage title="Sponsorship Inquiries" endpoint="/sponsors" testid="admin-sponsors"
            columns={[
              { k: "company_name", l: "Company" }, { k: "contact_name", l: "Contact" }, { k: "email", l: "Email" }, { k: "tier", l: "Tier" },
              { k: "created_at", l: "Date", r: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />
        } />
        <Route path="contacts" element={
          <SimpleListPage title="Contact Messages" endpoint="/contact" testid="admin-contacts"
            columns={[
              { k: "name", l: "Name" }, { k: "email", l: "Email" }, { k: "subject", l: "Subject" }, { k: "message", l: "Message" },
              { k: "created_at", l: "Date", r: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />
        } />
        <Route path="newsletter" element={
          <SimpleListPage title="Newsletter Subscribers" endpoint="/newsletter" testid="admin-newsletter"
            columns={[
              { k: "email", l: "Email" }, { k: "name", l: "Name" },
              { k: "created_at", l: "Subscribed", r: (r) => new Date(r.created_at).toLocaleDateString() },
            ]} />
        } />
      </Routes>
    </AdminShell>
  );
}
